import { useRef } from "react";
import { SECTIONS } from "../../constants/sections.jsx";
import { useThemeSlice } from "../ui/StatusBadge.jsx";
import {
  useActiveRoute,
  useNavigateTo,
  useOpenSec,
  useToggleOpenSec,
  useHoveredSec,
  useSetHoveredSec,
} from "../../logic/stores/navigationStore.js";
import { Ico } from "../icons/icons.jsx";
import { Tip } from "../ui/Tooltip.jsx";

export function SidebarNavigation({ SW, isOpen, isCollapsed, setHoveredTip, setTipRect }) {
  const { accentHex: A, c, dark, typography } = useThemeSlice();
  const active = useActiveRoute();
  const navigateTo = useNavigateTo();
  const openSec = useOpenSec();
  const toggleOpenSec = useToggleOpenSec();
  const hoveredSec = useHoveredSec();
  const setHoveredSec = useSetHoveredSec();
  const sectionRefs = useRef({});

  const navFont = typography.fontFamily;

  return (
    <nav style={{ flex: 1, overflowY: "auto", overflowX: "hidden", padding: "8px 0 4px", scrollbarWidth: "none", fontFamily: navFont }}>
      {SECTIONS.map((sec) => {
        const secOpen = openSec[sec.id];
        const anyActive = sec.children.some((ch) => ch.id === active);
        return (
          <div key={sec.id} style={{ position: "relative" }}>
            {SECTIONS.indexOf(sec) > 0 && isOpen && <div style={{ height: 1, background: c.divider, margin: "4px 0" }} />}
            {isCollapsed && hoveredSec === sec.id && null}

            {isOpen && (
              <button
                type="button"
                onClick={() => toggleOpenSec(sec.id)}
                onMouseEnter={(e) => (e.currentTarget.style.color = c.mid)}
                onMouseLeave={(e) => (e.currentTarget.style.color = c.sub)}
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "12px 14px 5px",
                  border: "none",
                  background: "transparent",
                  cursor: "pointer",
                  color: c.sub,
                  transition: "color .2s",
                }}
              >
                <span
                  style={{
                    fontFamily: navFont,
                    fontSize: 11,
                    fontWeight: 500,
                    letterSpacing: "0.13em",
                    textTransform: "uppercase",
                  }}
                >
                  {sec.label}
                </span>
                <span
                  style={{
                    display: "flex",
                    opacity: 0.5,
                    flexShrink: 0,
                    transform: secOpen ? "rotate(0deg)" : "rotate(-90deg)",
                    transition: "transform .2s",
                  }}
                >
                  {Ico.chevDown}
                </span>
              </button>
            )}

            {isCollapsed && (
              <div
                ref={(el) => {
                  sectionRefs.current[sec.id] = el;
                }}
                style={{ position: "relative" }}
              >
                {hoveredSec === sec.id && (
                  <div
                    onMouseEnter={() => setHoveredSec(sec.id)}
                    onMouseLeave={() => setHoveredSec(null)}
                    style={{
                      position: "fixed",
                      left: SW + 1,
                      top: sectionRefs.current[sec.id] ? sectionRefs.current[sec.id].getBoundingClientRect().top : 0,
                      zIndex: 500,
                      background: c.sb,
                      border: `1px solid ${c.sbBdr}`,
                      borderRadius: "0 10px 10px 0",
                      minWidth: 195,
                      boxShadow: dark ? "0 8px 32px rgba(0,0,0,.55)" : "0 8px 32px rgba(0,0,0,.12)",
                      overflow: "hidden",
                    }}
                  >
                    <div style={{ padding: "10px 14px 7px", borderBottom: `1px solid ${c.sbBdr}` }}>
                      <p
                        style={{
                          fontFamily: navFont,
                          fontSize: 11,
                          fontWeight: 500,
                          letterSpacing: "0.13em",
                          textTransform: "uppercase",
                          color: c.sub,
                          margin: 0,
                        }}
                      >
                        {sec.label}
                      </p>
                    </div>
                    <div style={{ padding: "4px 0" }}>
                      {sec.children.map((ch) => {
                        const isAct = active === ch.id;
                        return (
                          <button
                            key={ch.id}
                            type="button"
                            data-nav-route={ch.id}
                            onClick={() => {
                              navigateTo(ch.id);
                              setHoveredSec(null);
                            }}
                            onMouseEnter={(e) => {
                              if (!isAct) e.currentTarget.style.background = c.hover;
                            }}
                            onMouseLeave={(e) => {
                              if (!isAct) e.currentTarget.style.background = "transparent";
                            }}
                            style={{
                              width: "100%",
                              display: "flex",
                              alignItems: "center",
                              gap: 10,
                              padding: "8px 14px",
                              border: "none",
                              borderLeft: isAct ? `2.5px solid ${A}` : "2.5px solid transparent",
                              background: isAct ? `linear-gradient(90deg,${A}28,transparent)` : "transparent",
                              cursor: "pointer",
                              textAlign: "left",
                              color: isAct ? A : c.mid,
                              fontSize: 13,
                              fontWeight: isAct ? 600 : 400,
                            }}
                          >
                            <span style={{ color: isAct ? A : c.sub, display: "flex", flexShrink: 0 }}>{ch.icon}</span>
                            {ch.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
                {hoveredSec === sec.id ? (
                  <button
                    type="button"
                    onMouseEnter={() => setHoveredSec(sec.id)}
                    onMouseLeave={() => setHoveredSec(null)}
                    style={{
                      width: 54,
                      height: 38,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: 0,
                      border: "none",
                      borderLeft: anyActive ? `2.5px solid ${A}` : "2.5px solid transparent",
                      background: `linear-gradient(90deg,${A}18,transparent)`,
                      cursor: "pointer",
                      color: anyActive ? A : c.sub,
                    }}
                  >
                    <span style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 18, height: 18 }}>{sec.icon}</span>
                  </button>
                ) : (
                  <Tip setHoveredTip={setHoveredTip} setTipRect={setTipRect} id={`sec-${sec.id}`} label={sec.label} side="right">
                    <button
                      type="button"
                      onMouseEnter={() => {
                        setHoveredSec(sec.id);
                        setHoveredTip(null);
                      }}
                      onMouseLeave={() => setHoveredSec(null)}
                      style={{
                        width: 54,
                        height: 38,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: 0,
                        border: "none",
                        borderLeft: anyActive ? `2.5px solid ${A}` : "2.5px solid transparent",
                        background: anyActive ? `linear-gradient(90deg,${A}28,transparent)` : "transparent",
                        cursor: "pointer",
                        color: anyActive ? A : c.sub,
                      }}
                    >
                      <span style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 18, height: 18 }}>{sec.icon}</span>
                    </button>
                  </Tip>
                )}
              </div>
            )}

            {isOpen && (
              <div
                className="nemo-section-items"
                style={{
                  maxHeight: secOpen ? `${sec.children.length * 38}px` : "0px",
                  opacity: secOpen ? 1 : 0,
                  position: "relative",
                }}
              >
                {sec.children.map((ch) => {
                  const isAct = active === ch.id;
                  return (
                    <button
                      key={ch.id}
                      type="button"
                      data-nav-route={ch.id}
                      onMouseEnter={(e) => {
                        if (!isAct) e.currentTarget.style.background = c.hover;
                      }}
                      onMouseLeave={(e) => {
                        if (!isAct) e.currentTarget.style.background = "transparent";
                      }}
                      onClick={() => navigateTo(ch.id)}
                      style={{
                        width: "100%",
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        padding: "8px 14px",
                        border: "none",
                        borderLeft: isAct ? `2.5px solid ${A}` : "2.5px solid transparent",
                        background: isAct ? `linear-gradient(90deg,${A}28,transparent)` : "transparent",
                        cursor: "pointer",
                        textAlign: "left",
                        color: isAct ? A : c.mid,
                        fontSize: 13,
                        fontWeight: isAct ? 600 : 400,
                      }}
                    >
                      <span style={{ color: isAct ? A : c.sub, display: "flex", opacity: isAct ? 1 : 0.85 }}>{ch.icon}</span>
                      {ch.label}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </nav>
  );
}
